import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';

const ckeditorStyles = `
       <style>
        body {
          font-family: Arial, sans-serif;
          padding: 10px;
          margin: 0;
          color: rgba(0, 0, 0, 0.85);
          background-color: #fff;
        }
        h1, h2, h3, h4, h5, h6 {
          font-weight: bold;
        }
        p {
          margin: 0 0 10px;
        }
        ol {
          list-style-type: decimal;
        }
        ol ol {
          list-style-type: lower-latin;
        }
        ol ol ol {
          list-style-type: lower-roman;
        }
        ol ol ol ol {
          list-style-type: upper-latin;
        }
        ol ol ol ol ol {
          list-style-type: upper-roman;
        }
        ul {
          list-style-type: disc;
        }
        ul ul {
          list-style-type: circle;
        }
        ul ul ul,
        ul ul ul ul {
          list-style-type: square;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }
        th, td {
          border: 1px solid #ccc;
          padding: 6px 8px;
          text-align: left;
        }
        blockquote {
          margin: 1em 0;
          padding-left: 1em;
          border-left: 4px solid #ccc;
          color: #555;
        }
        a {
          color: #1890ff;
          text-decoration: underline;
        }
      </style>
    `;

const autoResizeScript = `
  <script>
    function resize() {
      document.body.style.overflow = 'hidden';
      const height = document.body.scrollHeight;
      window.parent.postMessage({ iframeHeight: height }, "*");
    }
    window.addEventListener("load", resize);
    setTimeout(resize, 50);
  <\/script>
`;

@Component({
  selector: 'app-instruction-view',
  templateUrl: './instruction-view.component.html',
  styleUrls: ['./instruction-view.component.scss'],
  imports: [NzIconModule],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstructionViewComponent implements AfterViewInit {
  private sanitizer = inject(DomSanitizer);
  private messageService = inject(NzMessageService);

  @ViewChild('iframeRef') iframeRef!: ElementRef<HTMLIFrameElement>;

  @Input()
  set instruction(value: string) {
    this.sanitizedInstruction = this.getSanitizedInstruction(value);
  }

  @Input()
  isShowCopyButton = false;

  sanitizedInstruction: SafeHtml = '';

  ngAfterViewInit() {
    window.addEventListener('message', this.messageHandler);
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.messageHandler);
  }

  copyInstruction(): void {
    const document = this.iframeRef?.nativeElement.contentDocument;
    const body = document?.body;

    if (!document || !body?.innerText.trim()) {
      return;
    }

    navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([body.innerHTML], { type: 'text/html' }),
        'text/plain': new Blob([body.innerText], { type: 'text/plain' }),
      }),
    ]);

    this.messageService.info('Скопировано в буфер обмена!');
  }

  getSanitizedInstruction(content: string | undefined): SafeHtml {
    if (!content) return '';

    const html =
      content.replace(
        /<a (.*?)href="(.*?)"(.*?)>/g,
        `<a $1 href="$2" $3 target="_blank" rel="noopener noreferrer">`,
      ) || null;

    const htmlWithStyle = `${ckeditorStyles} ${html} ${autoResizeScript}`;

    return this.sanitizer.bypassSecurityTrustHtml(htmlWithStyle);
  }

  private readonly messageHandler = (event: MessageEvent) => {
    const iframe = this.iframeRef?.nativeElement;

    if (
      iframe?.contentWindow &&
      event.source === iframe.contentWindow &&
      event.data?.iframeHeight
    ) {
      iframe.style.height = event.data.iframeHeight + 'px';
    }
  };
}
